export type AnnouncementModalConfig = {
  key: string;
  title: string;
  subtitle?: string;
  videoEmbedBaseUrl: string;
  videoBanner: string;
  videoA11yTitle: string;
  primaryCtaLabel: string;
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
};

type RequiredAnnouncementModalField = {
  [
    K in keyof AnnouncementModalConfig
  ]-?: undefined extends AnnouncementModalConfig[K]
    ? never
    : AnnouncementModalConfig[K] extends string
      ? K
      : never;
}[keyof AnnouncementModalConfig];

const STRING_FIELDS = [
  "key",
  "title",
  "subtitle",
  "videoEmbedBaseUrl",
  "videoBanner",
  "videoA11yTitle",
  "primaryCtaLabel",
  "secondaryCtaLabel",
  "secondaryCtaUrl",
] as const satisfies readonly (keyof AnnouncementModalConfig)[];

type _AssertStringFieldsComplete =
  Exclude<
    keyof AnnouncementModalConfig,
    (typeof STRING_FIELDS)[number]
  > extends never
    ? true
    : never;

const REQUIRED_FIELDS = [
  "key",
  "title",
  "videoEmbedBaseUrl",
  "videoBanner",
  "videoA11yTitle",
  "primaryCtaLabel",
] as const satisfies readonly RequiredAnnouncementModalField[];

type _AssertRequiredFieldsComplete =
  Exclude<
    RequiredAnnouncementModalField,
    (typeof REQUIRED_FIELDS)[number]
  > extends never
    ? true
    : never;

export const DEFAULT_ANNOUNCEMENT_MODAL_DISMISS_KEY =
  "showClarkWelcomeModal_v3";

/** E2e-only localStorage key; when set, the announcement modal never renders. */
export const ANNOUNCEMENT_MODAL_E2E_OVERRIDE_KEY = "announcementModal_override";

export const DEFAULT_ANNOUNCEMENT_MODAL_CONFIG: AnnouncementModalConfig = {
  key: DEFAULT_ANNOUNCEMENT_MODAL_DISMISS_KEY,
  title: "Introducing Superblocks 2.0",
  subtitle: "Get ready to build IT-approved internal AI apps in minutes",
  videoEmbedBaseUrl:
    "https://player.vimeo.com/video/1181374739?h=93441c50bd&title=0&byline=0&portrait=0&badge=0&dnt=1&controls=0&api=1",
  videoBanner:
    "See how Clark prepares your org to build Enterprise Apps in the AI era",
  videoA11yTitle: "Introducing Clark 2.0",
  primaryCtaLabel: "Start building",
};

function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasConfigContent(flagValue: Record<string, unknown>): boolean {
  return Object.keys(flagValue).length > 0;
}

export function isAnnouncementModalE2eOverrideActive(
  storage: Pick<Storage, "getItem"> = window.localStorage,
): boolean {
  try {
    return storage.getItem(ANNOUNCEMENT_MODAL_E2E_OVERRIDE_KEY) === "true";
  } catch {
    return false;
  }
}

export function buildVimeoAutoplayUrl(baseUrl: string): string {
  const url = new URL(baseUrl);
  url.searchParams.set("autoplay", "1");
  // Idle embed keeps controls=0 for a chromeless overlay; enable controls once playback starts.
  url.searchParams.set("controls", "1");
  return url.toString();
}

export function shouldShowAnnouncementModalFromStorage(
  dismissKey: string | undefined,
  storage: Pick<Storage, "getItem"> = window.localStorage,
): boolean {
  if (!dismissKey || isAnnouncementModalE2eOverrideActive(storage)) {
    return false;
  }

  try {
    return storage.getItem(dismissKey) !== "false";
  } catch {
    return true;
  }
}

function getMissingRequiredFields(
  config: Partial<AnnouncementModalConfig>,
): Array<RequiredAnnouncementModalField> {
  return REQUIRED_FIELDS.filter((field) => {
    const value = config[field];
    return typeof value !== "string" || value.length === 0;
  });
}

function warnInvalidAnnouncementModalConfig(message: string): void {
  console.warn(`[announcement-modal.config] ${message}`);
}

export function resolveAnnouncementModalConfig(
  flagValue: unknown,
): AnnouncementModalConfig | null {
  if (flagValue === undefined || flagValue === null) {
    return null;
  }

  if (!isRecord(flagValue) || !hasConfigContent(flagValue)) {
    warnInvalidAnnouncementModalConfig(
      "Ignoring flag value: expected a non-empty object.",
    );
    return null;
  }

  const config: Partial<AnnouncementModalConfig> = {};

  for (const field of STRING_FIELDS) {
    const value = flagValue[field];
    if (typeof value === "string" && value.length > 0) {
      config[field] = value;
    }
  }

  const missingRequiredFields = getMissingRequiredFields(config);
  if (missingRequiredFields.length > 0) {
    warnInvalidAnnouncementModalConfig(
      `Ignoring flag value: missing required fields: ${missingRequiredFields.join(", ")}.`,
    );
    return null;
  }

  if (!isSafeHttpUrl(config.videoEmbedBaseUrl!)) {
    warnInvalidAnnouncementModalConfig(
      "Ignoring flag value: videoEmbedBaseUrl must be an http or https URL.",
    );
    return null;
  }

  const resolved: AnnouncementModalConfig = {
    ...config,
  } as AnnouncementModalConfig;

  const secondaryLabel = resolved.secondaryCtaLabel;
  const secondaryUrl = resolved.secondaryCtaUrl;
  const hasSecondaryLabel = typeof secondaryLabel === "string";
  const hasSecondaryUrl = typeof secondaryUrl === "string";

  if (hasSecondaryLabel || hasSecondaryUrl) {
    if (
      !hasSecondaryLabel ||
      !hasSecondaryUrl ||
      !isSafeHttpUrl(secondaryUrl)
    ) {
      warnInvalidAnnouncementModalConfig(
        "Ignoring secondary CTA: secondaryCtaLabel and secondaryCtaUrl must both be present with a safe http or https URL.",
      );
      delete resolved.secondaryCtaLabel;
      delete resolved.secondaryCtaUrl;
    }
  }

  return resolved;
}
